/**
 * LessonIconProgress Component
 *
 * Displays an icon and progress for a lesson, with a button linking to the lesson resource.
 * The icon and button adapt based on user access rights and lesson progress state.
 *
 * Features:
 * - Dynamic link generation for lesson resource
 * - Conditional rendering of progress icon and button based on access rights
 * - Multi-language support via Tooltip
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Data object containing lesson info (idEmisor, idCourse, idVideo, title, desc)
 * @param {boolean} props.accessCourse - Whether the user has access to the course
 * @param {Array} props.userTrack - Array of user tracking objects for lesson progress
 * @returns {JSX.Element} The rendered lesson icon and progress
 */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CourseProgress from 'components/UI/CourseProgress/CourseProgress';
import classes from './LessonIconProgress.module.css';

const url = window.location.pathname.split('/');

const LessonIconProgress = props => {
  const dataAcademy = useSelector(state => state.academyInfo);
  const academyShortName = dataAcademy.data.shortName || url[1];
  const [activityState, setActivityState] = useState(
    'without_starting'
  );

  useEffect(() => {
    // Set lesson progress state based on userTrack
    props.userTrack.forEach(track => {
      if (track.id_media === props.data.idVideo) {
        if (track.media_play === 2) setActivityState('finished');
        else if (track.media_play === 1) setActivityState('started');
        else if (track.media_play === 0)
          setActivityState('without_starting');
      }
    });
  }, [props.userTrack]);

  return (
    <>
      <Tooltip placement="bottom" title="Lección">
        {url.includes('course') || url.includes('c') ? (
          <Button
            target="_blank"
            component="a"
            href={`https://lesson.mayahii.com/${academyShortName}/f/${props.data.idEmisor}/${props.data.idCourse}/${props.data.idVideo}`}
            className={`${classes.productLesson} ${
              !props.accessCourse
                ? classes.productLessonDisabled
                : ''
            }`}
          >
            <CourseProgress
              activityState={activityState} // Prop passed: lesson progress state
              icon="lesson" // Prop passed: icon type for lesson
            />
          </Button>
        ) : (
          <Button
            component="a"
            href={`https://lesson.mayahii.com/${academyShortName}/f/${props.data.idEmisor}/${props.data.idCourse}/${props.data.idVideo}`}
            className={`${classes.productLesson} ${
              !props.accessCourse
                ? classes.productLessonDisabled
                : ''
            }`}
          >
            <CourseProgress
              activityState={activityState} // Prop passed: lesson progress state
              icon="lesson" // Prop passed: icon type for lesson
            />
          </Button>
        )}
      </Tooltip>
      <p className={classes.productContentItemText}>
        {props.data.title}
        {/* Prop passed: lesson title */}
      </p>
      <p className={classes.descriptionText}>
        {props.data.desc}
        {/* Prop passed: lesson description */}
      </p>
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
LessonIconProgress.propTypes = {
  /**
   * Data object containing lesson info (idEmisor, idCourse, idVideo, title, desc)
   * Used to generate links and display lesson details
   * Passed as prop to Button, CourseProgress, and text elements
   */
  data: PropTypes.shape({
    idEmisor: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    idCourse: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    idVideo: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    title: PropTypes.string,
    desc: PropTypes.string
  }),
  /**
   * Boolean indicating whether the user has access to the course
   * Controls button and icon rendering/styling
   * Passed as prop to Button and CourseProgress
   */
  accessCourse: PropTypes.bool,
  /**
   * Array of user tracking objects for lesson progress
   * Used to determine lesson progress state
   * Passed as prop to internal logic
   */
  userTrack: PropTypes.arrayOf(PropTypes.object)
};

LessonIconProgress.defaultProps = {
  data: {
    idEmisor: '',
    idCourse: '',
    idVideo: '',
    title: '',
    desc: ''
  },
  accessCourse: false,
  userTrack: []
};

export default LessonIconProgress;
